import type { ComponentChildren } from 'preact';
import { Battery, BatteryLow, Flag, FlagTriangleRight, Gauge, Route as RouteIcon } from 'lucide-preact';
import { routeStore } from '../state/routeStore';
import { playbackProgress } from '../state/playback';
import { activeDistanceMeters, activeWaypoint, displayAverageKmh, formatWaypointTime } from '../lib/activeWaypoint';
import type { Route, RouteWaypoint } from '../backend/route';
import { t } from '../i18n';

/**
 * Two exports: `StatsTopRow` (the 3-cell grid — speed / distance / battery)
 * is the anchor row, used both as the bottom-sheet peek and as the first
 * row when the sheet is expanded. `StatsAddresses` (start / arrival with
 * inline timestamps) only renders below the row when the sheet is open.
 *
 * Same data source, different density. Hidden fields collapse cleanly — we
 * never fake a number we don't have.
 */

/**
 * Once the journey is finished and we're not replaying, the last waypoint's
 * instantaneous speed is misleading — it's the speed at the moment of the
 * final GPS fix, usually near zero because the traveller had already
 * stopped. Surface the journey's average instead. During replay (progress
 * is non-null) we go back to the head's live speed so the cell tracks the
 * map marker.
 *
 * Returned value is in km/h. `waypoint.spd` is already stored in km/h by the
 * API (it converts the device's m/s fix with `coords.speed * 3.6` on the way
 * in), so it's passed through as-is. The journey average comes from
 * `displayAverageKmh`, which prefers the server-computed `average_speed`
 * (api #180) and falls back to the client-side estimate for older routes.
 */
function speedForView(
  route: Route | null,
  active: RouteWaypoint | null,
  progress: number | null,
): { speed: number | null; isAverage: boolean } {
  const isReplaying = progress !== null;
  if (route?.finished && !isReplaying) {
    const avg = displayAverageKmh(route);
    if (avg !== null) return { speed: avg, isAverage: true };
  }
  return { speed: active?.spd ?? null, isAverage: false };
}

export function StatsTopRow() {
  const route = routeStore.value;
  const progress = playbackProgress.value;
  const active = activeWaypoint(route, progress);
  const distance = activeDistanceMeters(route, progress);
  const { speed: speedValue, isAverage: speedIsAverage } = speedForView(route, active, progress);
  return (
    <dl class="grid grid-cols-3 gap-3 text-sm">
      <StatRow
        label={speedIsAverage ? t('stats.avgSpeed') : t('stats.speed')}
        icon={<Gauge size={16} />}
      >
        {formatSpeed(speedValue)}
      </StatRow>
      <StatRow label={t('stats.distance')} icon={<RouteIcon size={16} />}>
        {formatDistance(distance)}
      </StatRow>
      <StatRow label={t('stats.battery')} icon={batteryIcon(active?.bat ?? null)}>
        {formatBattery(active?.bat ?? null)}
      </StatRow>
    </dl>
  );
}

export function StatsAddresses() {
  const route = routeStore.value;
  const startAddress = route?.address_start?.trim() || null;
  const finishAddress = route?.address_arrival?.trim() || null;
  if (!startAddress && !finishAddress) return null;
  // Timestamps come from the first/last waypoints (more precise than
  // route.created_at / finished_at, which lag behind by a few ms).
  const firstWaypoint = route?.waypoints?.[0];
  const lastWaypoint = route?.waypoints?.[route.waypoints.length - 1];
  const startTime = formatWaypointTime(firstWaypoint?.time) ?? formatWaypointTime(route?.created_at);
  const finishTime = route?.finished
    ? formatWaypointTime(lastWaypoint?.time) ?? formatWaypointTime(route.finished_at ?? undefined)
    : null;
  return (
    <dl class="space-y-2 text-sm">
      {startAddress && (
        <InfoRow
          icon={<Flag size={14} class="text-primary" />}
          label={t('stats.startAddress')}
          value={startAddress}
          meta={startTime}
        />
      )}
      {finishAddress && (
        <InfoRow
          icon={<FlagTriangleRight size={14} class="text-safety" />}
          label={t('stats.finishAddress')}
          value={finishAddress}
          meta={finishTime}
        />
      )}
    </dl>
  );
}

function InfoRow({
  icon,
  label,
  value,
  meta,
}: {
  icon: ComponentChildren;
  label: string;
  value: string;
  /** Optional secondary text shown right-aligned next to the label (e.g. timestamp). */
  meta?: string | null;
}) {
  return (
    <div class="flex items-start gap-2.5 rounded-xl bg-surface-muted px-3 py-2.5">
      <span class="shrink-0 mt-0.5" aria-hidden="true">{icon}</span>
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline justify-between gap-2">
          <dt class="text-xs font-medium text-text-secondary">{label}</dt>
          {meta && <span class="text-xs text-text-secondary tabular-nums shrink-0">{meta}</span>}
        </div>
        <dd class="text-text leading-snug break-words">{value}</dd>
      </div>
    </div>
  );
}

function StatRow({
  label,
  icon,
  children,
  class: extraClass,
}: {
  label: string;
  icon: ComponentChildren;
  children: ComponentChildren;
  class?: string;
}) {
  return (
    <div class={`flex items-center gap-2.5 rounded-xl bg-surface-muted px-3 py-2.5 ${extraClass ?? ''}`}>
      <span class="text-primary shrink-0" aria-hidden="true">
        {icon}
      </span>
      <div class="min-w-0">
        <dt class="text-xs font-medium text-text-secondary">{label}</dt>
        <dd class="font-semibold text-text text-base leading-tight">{children}</dd>
      </div>
    </div>
  );
}

function batteryIcon(battery: number | null) {
  if (battery !== null && battery <= 20) return <BatteryLow size={14} aria-hidden="true" />;
  return <Battery size={14} aria-hidden="true" />;
}

function formatBattery(battery: number | null): string {
  if (battery === null) return '—';
  return `${Math.round(battery)}%`;
}

function formatSpeed(kmh: number | null): string {
  if (kmh === null || kmh < 0) return '—';
  if (kmh < 0.5) return '0 km/h';
  return `${Math.round(kmh)} km/h`;
}

function formatDistance(meters: number | null): string {
  if (meters === null || meters === undefined) return '—';
  if (meters < 1_000) return `${Math.round(meters)} m`;
  return `${(meters / 1_000).toFixed(meters < 10_000 ? 1 : 0)} km`;
}
